import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "types";
import {
  STATUS_TABS,
  VehicleStatusTab,
  YARD_STATUS_TABS,
  YardStatusTab,
} from "../types";
import { initialState } from "./initialState";

const IN_YARD_TAB = STATUS_TABS[0];
const WAITING_ENTRY_TAB = STATUS_TABS[1];

const selectDomain = (state?: RootState) =>
  state?.vehicleStatusesState || initialState;

export const selectVehicleStatusesLoading = createSelector(
  [selectDomain],
  (state) => state.loading
);

export const selectSearchQuery = createSelector(
  [selectDomain],
  (state) => state.searchQuery
);

export const selectYardSearchQuery = createSelector(
  [selectDomain],
  (state) => state.yardSearchQuery
);

export const selectIsSearchExtended = createSelector(
  [selectDomain],
  (state) => state.isSearchExtended
);

export const selectIsYardSearchExtended = createSelector(
  [selectDomain],
  (state) => state.isYardSearchExtended
);

export const selectActiveStatusTab = createSelector(
  [selectDomain],
  (state) => state.activeStatusTab
);

export const selectIsYardTab = createSelector(
  [selectActiveStatusTab],
  (activeStatusTab) => activeStatusTab === IN_YARD_TAB
);

export const selectActiveYardTab = createSelector(
  [selectDomain],
  (state) => state.activeYardTab
);

export const selectActiveDetailTab = createSelector(
  [selectDomain],
  (state) => state.activeDetailTab
);

export const selectStatCards = createSelector(
  [selectDomain],
  (state) => state.statCards
);

const selectVehicles = createSelector(
  [selectDomain],
  (state) => state.vehicles
);

export const selectStatusTabCounts = createSelector(
  [selectVehicles],
  (vehicles) =>
    vehicles.reduce(
      (counts, vehicle) => {
        if (vehicle.statusTab === WAITING_ENTRY_TAB) {
          counts[WAITING_ENTRY_TAB] += 1;
        } else {
          counts[IN_YARD_TAB] += 1;
        }
        return counts;
      },
      STATUS_TABS.reduce(
        (counts, tab) => {
          counts[tab] = 0;
          return counts;
        },
        {} as Record<VehicleStatusTab, number>
      )
    )
);

export const selectYardTabCounts = createSelector(
  [selectVehicles],
  (vehicles) =>
    vehicles.reduce(
      (counts, vehicle) => {
        if (vehicle.statusTab !== WAITING_ENTRY_TAB) {
          counts[vehicle.statusTab] += 1;
        }
        return counts;
      },
      YARD_STATUS_TABS.reduce(
        (counts, tab) => {
          counts[tab] = 0;
          return counts;
        },
        {} as Record<YardStatusTab, number>
      )
    )
);

const matchesSearch = (
  vehicle: (typeof initialState.vehicles)[number],
  query: string
) =>
  [
    vehicle.workOrderNo,
    vehicle.chassisNo,
    vehicle.customerName,
    vehicle.makeModel,
    vehicle.manufacturer,
    vehicle.model,
    vehicle.vehicleType,
  ].some((field) => field.toLowerCase().includes(query));

export const selectFilteredVehicles = createSelector(
  [
    selectVehicles,
    selectIsYardTab,
    selectActiveStatusTab,
    selectActiveYardTab,
    selectSearchQuery,
    selectYardSearchQuery,
  ],
  (
    vehicles,
    isYardTab,
    activeStatusTab,
    activeYardTab,
    searchQuery,
    yardSearchQuery
  ) => {
    const byTab = isYardTab
      ? vehicles.filter((vehicle) => vehicle.statusTab === activeYardTab)
      : vehicles.filter((vehicle) => vehicle.statusTab === activeStatusTab);

    const query = (isYardTab ? yardSearchQuery : searchQuery)
      .trim()
      .toLowerCase();

    if (!query) {
      return byTab;
    }

    return byTab.filter((vehicle) => matchesSearch(vehicle, query));
  }
);

export const selectSelectedVehicleDetail = createSelector(
  [selectDomain],
  (state) => state.selectedVehicleDetail
);

export const selectProductionTab = createSelector(
  [selectSelectedVehicleDetail],
  (detail) => (detail ? detail.production : null)
);

export const selectWeightsTab = createSelector(
  [selectSelectedVehicleDetail],
  (detail) => (detail ? detail.weights : null)
);

export const selectTestsTab = createSelector(
  [selectSelectedVehicleDetail],
  (detail) => (detail ? detail.tests : null)
);

export const selectNotesTab = createSelector(
  [selectSelectedVehicleDetail],
  (detail) => (detail ? detail.notes : null)
);

export const selectDetailsTab = createSelector(
  [selectSelectedVehicleDetail],
  (detail) => (detail ? detail.sections : null)
);
